import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { SessionProvider, useSession } from './state/session'
import { TodayProvider } from './state/today'
import { ToastProvider } from './state/toast'
import { AppShell } from './components/AppShell'
import { Spinner } from './components/ui'
import LoginPage from './pages/Login'
import SetPasswordPage from './pages/SetPassword'
import TodayPage from './pages/Today'
import NewNotePage from './pages/NewNote'
import LogPage from './pages/Log'
import InventoryPage from './pages/Inventory'
import SettingsPage from './pages/Settings'
import DocumentsPage from './pages/Documents'
import SiteReportPage from './pages/admin/SiteReport'
import AdminLayout from './pages/admin/AdminLayout'
import AdminDashboard from './pages/admin/Dashboard'
import AdminNotes from './pages/admin/Notes'
import AdminPeople from './pages/admin/People'
import AdminSites from './pages/admin/Sites'
import AdminSchedules from './pages/admin/Schedules'
import AdminInventory from './pages/admin/Inventory'
import AdminTimesheet from './pages/admin/Timesheet'
import AdminSettings from './pages/admin/Settings'
import AdminExport from './pages/admin/Export'

/** Lets the service worker steer an already-open app when a reminder is tapped. */
function ServiceWorkerNavigation() {
  const navigate = useNavigate()

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | undefined
      if (data?.type === 'NAVIGATE' && data.url) navigate(data.url)
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [navigate])

  return null
}

function Protected() {
  const { user, loading } = useSession()

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Loading your day" />
      </div>
    )
  }
  if (!user) return <Navigate to="/login" replace />
  if (user.mustChangePassword) return <Navigate to="/set-password" replace />

  return (
    <TodayProvider>
      <AppShell />
    </TodayProvider>
  )
}

function ReportOnly() {
  const { user, loading } = useSession()
  if (loading) return <div className="flex min-h-dvh items-center justify-center"><Spinner /></div>
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'admin') return <Navigate to="/" replace />
  return <SiteReportPage />
}

function AdminOnly() {
  const { user } = useSession()
  if (user && user.role !== 'admin') return <Navigate to="/" replace />
  return <AdminLayout />
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <SessionProvider>
          <ServiceWorkerNavigation />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route element={<Protected />}>
              <Route index element={<TodayPage />} />
              <Route path="note/new" element={<NewNotePage />} />
              <Route path="log" element={<LogPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="docs" element={<DocumentsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="admin" element={<AdminOnly />}>
                <Route index element={<AdminDashboard />} />
                <Route path="notes" element={<AdminNotes />} />
                <Route path="people" element={<AdminPeople />} />
                <Route path="sites" element={<AdminSites />} />
                <Route path="schedules" element={<AdminSchedules />} />
                <Route path="inventory" element={<AdminInventory />} />
                <Route path="timesheet" element={<AdminTimesheet />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="export" element={<AdminExport />} />
              </Route>
            </Route>
            {/* Outside the app shell: a bare page that prints cleanly to PDF. */}
            <Route path="/report" element={<ReportOnly />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SessionProvider>
      </ToastProvider>
    </BrowserRouter>
  )
}
